import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import {
  ScanLine,
  Camera,
  Clock,
  ChevronRight,
  Plus,
  X,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ExternalLink,
  Trash2,
  ChevronLeft,
  ImageIcon,
  FileQuestion,
  FileText,
} from 'lucide-react'
import { QuickScanCamera } from '../../components/parent/QuickScanCamera'
import { QuickScanResult } from '../../components/parent/QuickScanResult'
import type {
  TonightsReviewResponse,
  TonightScan,
  ProcessScanResponse,
} from '../../lib/api-types'

// ── Types ───────────────────────────────────────────────

interface StudentScanSummary {
  id: number
  name: string
  total_scans: number
  recent_count: number
  uncategorized_count: number
  total_xp_from_scans: number
}

interface UnlinkedScan {
  id: number
  file_name: string
  detected_title: string | null
  detected_score: number | null
  scan_date: string | null
  source: string
  dropbox_url: string | null
  drive_url: string | null
  file_path: string | null
  mime_type: string | null
  ocr_preview: string | null
}

interface FlatScan extends TonightScan {
  studentId: number
  studentName: string
}

// Student color palette for name tags
const STUDENT_COLORS = [
  { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
  { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
  { bg: 'bg-teal-500/20', text: 'text-teal-300', border: 'border-teal-500/30' },
  { bg: 'bg-pink-500/20', text: 'text-pink-300', border: 'border-pink-500/30' },
]

function getStudentColor(index: number) {
  return STUDENT_COLORS[index % STUDENT_COLORS.length]
}

// ── Main Page ───────────────────────────────────────────

export default function ParentScans() {
  const queryClient = useQueryClient()

  // Scan camera state
  const [scanState, setScanState] = useState<'idle' | 'select-student' | 'camera' | 'result'>('idle')
  const [selectedStudentId, setSelectedStudentId] = useState<number | null>(null)
  const [capturedImages, setCapturedImages] = useState<string[]>([])
  const [showToast, setShowToast] = useState(false)

  // Data queries
  const { data: tonightData, isLoading: tonightLoading } = useQuery<TonightsReviewResponse>({
    queryKey: ['tonight-review'],
    queryFn: async () => {
      const response = await api.get('/api/homework/tonight?days=7&include_pending=true')
      return response.data
    },
  })

  const { data: overviewData, isLoading: overviewLoading } = useQuery<{ students: StudentScanSummary[] }>({
    queryKey: ['parent-scans-overview'],
    queryFn: async () => {
      const response = await api.get('/api/parent/scans-overview')
      return response.data
    },
  })

  const { data: unlinkedData } = useQuery<{ scans: UnlinkedScan[]; count: number }>({
    queryKey: ['parent-unlinked-scans'],
    queryFn: async () => {
      const response = await api.get('/api/parent/unlinked-scans')
      return response.data
    },
  })

  const students = tonightData?.students || []
  const overviewStudents = overviewData?.students || []
  const unlinkedScans = unlinkedData?.scans || []

  // Build student color map
  const studentColorMap = new Map<number, ReturnType<typeof getStudentColor>>()
  students.forEach((s, i) => studentColorMap.set(s.id, getStudentColor(i)))

  // Flatten scans across all students and sort by actionability then date
  const flatScans: FlatScan[] = students.flatMap((student) =>
    student.scans.map((scan) => ({
      ...scan,
      studentId: student.id,
      studentName: student.name,
    }))
  )

  // Sort: action needed first (ungraded, ready for review), then by date desc
  const actionNeeded = flatScans.filter((s) => {
    const session = s.homework_session
    if (!session) return true // needs grading
    if (session.status === 'failed') return true
    if (session.status === 'ready_for_review' && !session.reviewed_by_parent) return true
    return false
  })

  const completed = flatScans.filter((s) => {
    const session = s.homework_session
    if (!session) return false
    if (session.status === 'failed') return false
    if (session.status === 'ready_for_review' && !session.reviewed_by_parent) return false
    return true
  })

  const isLoading = tonightLoading || overviewLoading

  // Scan flow handlers
  const startScan = () => {
    if (students.length === 1) {
      setSelectedStudentId(students[0].id)
      setScanState('camera')
    } else if (students.length > 1) {
      setScanState('select-student')
    }
  }

  const handleScanSuccess = () => {
    setScanState('idle')
    setCapturedImages([])
    setSelectedStudentId(null)
    setShowToast(true)
    setTimeout(() => setShowToast(false), 3000)
    queryClient.invalidateQueries({ queryKey: ['tonight-review'] })
    queryClient.invalidateQueries({ queryKey: ['parent-scans-overview'] })
  }

  return (
    <div className="space-y-6">
      {/* Header with scan button */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-quest-primary/20 flex items-center justify-center">
            <ScanLine className="w-5 h-5 text-quest-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Scans</h1>
            <p className="text-sm text-gray-400">Scan, grade, and review</p>
          </div>
        </div>
        <button
          onClick={startScan}
          disabled={students.length === 0}
          className="btn btn-primary flex items-center gap-2 text-sm"
        >
          <Plus className="w-4 h-4" />
          <Camera className="w-4 h-4" />
        </button>
      </div>

      {isLoading && (
        <div className="animate-pulse space-y-4">
          <div className="h-24 bg-surface-card rounded-xl"></div>
          <div className="h-32 bg-surface-card rounded-xl"></div>
          <div className="h-32 bg-surface-card rounded-xl"></div>
        </div>
      )}

      {!isLoading && (
        <>
          {/* Unlinked Scans Banner */}
          {unlinkedScans.length > 0 && (
            <UnlinkedScansSection
              unlinkedScans={unlinkedScans}
              students={students.map((s) => ({ id: s.id, name: s.name }))}
              queryClient={queryClient}
            />
          )}

          {/* Action Needed */}
          {actionNeeded.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Action Needed
              </h2>
              <div className="space-y-2">
                {actionNeeded.map((scan) => (
                  <ScanCard
                    key={`${scan.studentId}-${scan.scan_id}`}
                    scan={scan}
                    studentColor={studentColorMap.get(scan.studentId)}
                    showStudent={students.length > 1}
                    queryClient={queryClient}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Completed / Recent */}
          {completed.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                Recent
              </h2>
              <div className="space-y-2">
                {completed.map((scan) => (
                  <ScanCard
                    key={`${scan.studentId}-${scan.scan_id}`}
                    scan={scan}
                    studentColor={studentColorMap.get(scan.studentId)}
                    showStudent={students.length > 1}
                    queryClient={queryClient}
                  />
                ))}
              </div>
            </section>
          )}

          {/* Empty state */}
          {flatScans.length === 0 && unlinkedScans.length === 0 && (
            <div className="card text-center py-12">
              <Camera className="w-12 h-12 mx-auto mb-4 text-gray-600" />
              <p className="text-gray-400 text-lg mb-2">No recent scans</p>
              <p className="text-gray-500 text-sm mb-4">
                Photograph homework or tests to get started
              </p>
              <button
                onClick={startScan}
                disabled={students.length === 0}
                className="btn btn-primary inline-flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Scan Now
              </button>
            </div>
          )}

          {/* History - per student stats */}
          {overviewStudents.length > 0 && (
            <section>
              <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
                History
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {overviewStudents.map((student) => (
                  <Link
                    key={student.id}
                    to={`/parent/courses/student/${student.id}/scans`}
                    className="card flex items-center justify-between py-3 hover:border-quest-primary/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-quest-primary/20 flex items-center justify-center text-sm font-bold">
                        {student.name.charAt(0)}
                      </div>
                      <div>
                        <p className="font-medium text-sm">{student.name}</p>
                        <p className="text-xs text-gray-400">
                          {student.total_scans} scans &middot; {student.recent_count} this week
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-quest-accent font-medium">+{student.total_xp_from_scans} XP</span>
                      <ChevronRight className="w-4 h-4 text-gray-400" />
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {/* Student selector modal */}
      {scanState === 'select-student' && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="card max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Scan for which child?</h3>
              <button onClick={() => setScanState('idle')} className="p-2 hover:bg-surface-hover rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-2">
              {students.map((student) => (
                <button
                  key={student.id}
                  onClick={() => { setSelectedStudentId(student.id); setScanState('camera') }}
                  className="w-full p-4 text-left rounded-lg bg-surface-card hover:bg-surface-hover transition-colors flex items-center justify-between"
                >
                  <span className="font-medium">{student.name}</span>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Camera overlay */}
      {scanState === 'camera' && (
        <QuickScanCamera
          onCapture={(imageData) => {
            setCapturedImages(prev => [...prev, imageData])
            setScanState('result')
          }}
          onClose={() => {
            if (capturedImages.length > 0) {
              setScanState('result')
            } else {
              setScanState('idle')
              setSelectedStudentId(null)
            }
          }}
          pageNumber={capturedImages.length + 1}
          existingPages={capturedImages.length}
        />
      )}

      {/* Result overlay */}
      {scanState === 'result' && capturedImages.length > 0 && selectedStudentId && (
        <QuickScanResult
          images={capturedImages}
          studentId={selectedStudentId}
          onConfirm={handleScanSuccess}
          onEdit={() => {
            setScanState('idle')
            setCapturedImages([])
          }}
          onCancel={() => {
            setScanState('idle')
            setCapturedImages([])
            setSelectedStudentId(null)
          }}
          onAddPage={() => setScanState('camera')}
        />
      )}

      {/* Success toast */}
      {showToast && (
        <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 bg-green-600 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          Saved!
        </div>
      )}
    </div>
  )
}

// ── Scan Card ───────────────────────────────────────────

function ScanCard({
  scan,
  studentColor,
  showStudent,
  queryClient,
}: {
  scan: FlatScan
  studentColor?: ReturnType<typeof getStudentColor>
  showStudent: boolean
  queryClient: ReturnType<typeof useQueryClient>
}) {
  const [ocrExpanded, setOcrExpanded] = useState(false)
  const [processingError, setProcessingError] = useState<string | null>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [isGrading, setIsGrading] = useState(false)

  const processMutation = useMutation<ProcessScanResponse, Error, { force?: boolean }>({
    mutationFn: async ({ force = false } = {}) => {
      const response = await api.post(`/api/homework/scans/${scan.scan_id}/process`, { force })
      return response.data
    },
    onSuccess: (data) => {
      setProcessingError(null)
      if (data.status === 'processing') {
        setIsGrading(true)
        const pollInterval = setInterval(async () => {
          try {
            const res = await api.get(`/api/homework/scans/${scan.scan_id}/status`)
            if (res.data.status !== 'processing') {
              clearInterval(pollInterval)
              setIsGrading(false)
              queryClient.invalidateQueries({ queryKey: ['tonight-review'] })
            }
          } catch {
            clearInterval(pollInterval)
            setIsGrading(false)
            setProcessingError('Failed to check grading status')
          }
        }, 5000)
      } else {
        queryClient.invalidateQueries({ queryKey: ['tonight-review'] })
      }
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setProcessingError(err.response?.data?.error || 'Failed to process scan')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: async () => {
      await api.delete(`/api/parent/students/${scan.studentId}/scans/${scan.scan_id}`)
    },
    onSuccess: () => {
      setConfirmDelete(false)
      queryClient.invalidateQueries({ queryKey: ['tonight-review'] })
      queryClient.invalidateQueries({ queryKey: ['parent-scans-overview'] })
    },
  })

  const session = scan.homework_session
  const parsed = scan.parsed
  const hasSession = !!session
  const isReviewed = session?.reviewed_by_parent
  const isReadyForReview = session?.status === 'ready_for_review' && !isReviewed
  const isFailed = session?.status === 'failed'

  // Status badge
  let statusBadge = null
  if (isReviewed) {
    statusBadge = (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400">
        <CheckCircle className="w-3 h-3" /> Reviewed
      </span>
    )
  } else if (isReadyForReview) {
    statusBadge = (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
        <Clock className="w-3 h-3" /> Ready for Review
      </span>
    )
  } else if (processMutation.isPending || isGrading) {
    statusBadge = (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400">
        <Loader2 className="w-3 h-3 animate-spin" /> {isGrading ? 'Grading...' : 'Starting...'}
      </span>
    )
  } else if (isFailed) {
    statusBadge = (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-400">
        Failed
      </span>
    )
  } else if (!hasSession) {
    statusBadge = (
      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">
        Ungraded
      </span>
    )
  }

  // Title
  let displayTitle = scan.title || 'Untitled Scan'
  if (parsed.subject && parsed.lesson_number) {
    displayTitle = `${parsed.subject}${parsed.curriculum ? ' - ' + parsed.curriculum : ''}, Lesson ${parsed.lesson_number}`
    if (parsed.scan_type === 'test') {
      displayTitle = `${parsed.subject}${parsed.curriculum ? ' - ' + parsed.curriculum : ''}, Test ${parsed.lesson_number}`
    }
  }

  // Score
  let scoreDisplay = null
  if (session && session.total_questions > 0) {
    const pct = Math.round((session.correct_count / session.total_questions) * 100)
    const color = pct >= 80 ? 'text-green-400' : pct >= 60 ? 'text-yellow-400' : 'text-red-400'
    scoreDisplay = (
      <span className={`text-sm font-medium ${color}`}>
        {session.correct_count}/{session.total_questions} ({pct}%)
      </span>
    )
  }

  const color = studentColor || STUDENT_COLORS[0]

  return (
    <div className="rounded-lg border border-gray-700/50 bg-surface-card/50 px-4 py-3">
      {/* Top row: student tag + title + status */}
      <div className="flex items-center gap-2 flex-wrap">
        {showStudent && (
          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${color.bg} ${color.text}`}>
            {scan.studentName}
          </span>
        )}
        <h3 className="font-medium text-sm flex-1 min-w-0 truncate">{displayTitle}</h3>
        {statusBadge}
        {scoreDisplay}
      </div>

      {/* Course files */}
      {scan.course_files.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {scan.course_files.map((file) => (
            <a
              key={file.id}
              href={file.url || '#'}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded bg-gray-700 hover:bg-gray-600 text-gray-300 transition-colors"
            >
              <FileText className="w-3 h-3" />
              {file.name}
              <ExternalLink className="w-2.5 h-2.5" />
            </a>
          ))}
        </div>
      )}

      {/* OCR preview */}
      {scan.ocr_preview && (
        <div className="mt-1.5">
          <button
            onClick={() => setOcrExpanded(!ocrExpanded)}
            className="text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1"
          >
            {ocrExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            OCR Preview
          </button>
          {ocrExpanded && (
            <div className="mt-1 text-xs text-gray-400 bg-surface-base rounded p-2 font-mono whitespace-pre-wrap">
              {scan.ocr_preview}
            </div>
          )}
        </div>
      )}

      {/* Error */}
      {processingError && (
        <div className="mt-2 text-sm text-red-400 bg-red-500/10 rounded p-2">
          {processingError}
        </div>
      )}

      {/* Actions */}
      <div className="mt-2 flex items-center gap-2">
        {(!hasSession || isFailed) && !processMutation.isPending && !isGrading && (
          <button
            onClick={() => processMutation.mutate({ force: isFailed })}
            className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {isFailed ? 'Retry' : 'Grade with AI'}
          </button>
        )}

        {isReadyForReview && session && (
          <>
            <Link
              to={`/parent/scans/${session.id}`}
              className="btn-primary text-xs py-1.5 px-3 flex items-center gap-1.5"
            >
              Review
            </Link>
            <button
              onClick={() => processMutation.mutate({ force: true })}
              className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              Re-grade
            </button>
          </>
        )}

        {isReviewed && session && (
          <>
            <Link
              to={`/parent/scans/${session.id}`}
              className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
            >
              View Results
            </Link>
            <button
              onClick={() => processMutation.mutate({ force: true })}
              className="text-xs text-gray-400 hover:text-gray-300 flex items-center gap-1"
            >
              <Sparkles className="w-3 h-3" />
              Re-grade
            </button>
          </>
        )}

        {!confirmDelete ? (
          <button
            onClick={() => setConfirmDelete(true)}
            className="ml-auto text-gray-500 hover:text-red-400 flex items-center gap-1 transition-colors"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-gray-400">Delete?</span>
            <button
              onClick={() => deleteMutation.mutate()}
              disabled={deleteMutation.isPending}
              className="text-xs px-2 py-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
            >
              {deleteMutation.isPending ? '...' : 'Yes'}
            </button>
            <button
              onClick={() => setConfirmDelete(false)}
              className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-400 hover:bg-gray-600"
            >
              No
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Unlinked Scans Section ──────────────────────────────

function UnlinkedScansSection({
  unlinkedScans,
  students,
  queryClient,
}: {
  unlinkedScans: UnlinkedScan[]
  students: { id: number; name: string }[]
  queryClient: ReturnType<typeof useQueryClient>
}) {
  const [expanded, setExpanded] = useState(false)
  const [selectedScan, setSelectedScan] = useState<UnlinkedScan | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageLoading, setImageLoading] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)

  const assignMutation = useMutation({
    mutationFn: async ({ scanId, studentId }: { scanId: number; studentId: number }) => {
      const response = await api.post(`/api/parent/scans/${scanId}/assign`, { student_id: studentId })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parent-unlinked-scans'] })
      queryClient.invalidateQueries({ queryKey: ['tonight-review'] })
      queryClient.invalidateQueries({ queryKey: ['parent-scans-overview'] })
      if (imageUrl) URL.revokeObjectURL(imageUrl)
      setImageUrl(null)
      setSelectedScan(null)
    },
    onError: (error: Error & { response?: { data?: { error?: string } } }) => {
      alert(error.response?.data?.error || 'Failed to assign scan.')
    },
  })

  const fetchImage = async (scanId: number, page = 1) => {
    setImageLoading(true)
    setImageError(false)
    setImageUrl(null)
    try {
      const infoResponse = await api.get(`/api/parent/unlinked-scans/${scanId}/image?info=true`)
      setTotalPages(infoResponse.data.total_pages || 1)
      const response = await api.get(`/api/parent/unlinked-scans/${scanId}/image?page=${page}`, { responseType: 'blob' })
      const url = URL.createObjectURL(response.data)
      setImageUrl(url)
      setCurrentPage(page)
    } catch {
      setImageError(true)
    } finally {
      setImageLoading(false)
    }
  }

  const handleSelect = (scan: UnlinkedScan | null) => {
    if (imageUrl) URL.revokeObjectURL(imageUrl)
    setImageUrl(null)
    setImageError(false)
    setCurrentPage(1)
    setTotalPages(1)
    setSelectedScan(scan)
    if (scan) fetchImage(scan.id, 1)
  }

  return (
    <div className="rounded-lg border border-orange-500/30 bg-orange-900/10 overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3"
      >
        <div className="flex items-center gap-2">
          <FileQuestion className="w-5 h-5 text-orange-400" />
          <span className="font-medium text-orange-300 text-sm">
            {unlinkedScans.length} unlinked scan{unlinkedScans.length !== 1 ? 's' : ''}
          </span>
          <span className="text-xs text-orange-400/70">- assign to a child</span>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-orange-400" /> : <ChevronDown className="w-4 h-4 text-orange-400" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4">
          {selectedScan ? (
            <div className="space-y-3">
              <button
                onClick={() => handleSelect(null)}
                className="text-sm text-gray-400 hover:text-white flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>

              {/* Image viewer */}
              <div className="bg-surface-card rounded-lg overflow-hidden">
                <div className="aspect-[3/4] relative bg-surface-base flex items-center justify-center">
                  {imageLoading ? (
                    <Loader2 className="w-8 h-8 animate-spin text-quest-primary" />
                  ) : imageError ? (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <ImageIcon className="w-12 h-12" />
                      <p className="text-sm">Image not available</p>
                    </div>
                  ) : imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={selectedScan.detected_title || selectedScan.file_name}
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <ImageIcon className="w-12 h-12" />
                      <p className="text-sm">No image</p>
                    </div>
                  )}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-4 p-2 border-t border-gray-700/50">
                    <button
                      onClick={() => fetchImage(selectedScan.id, currentPage - 1)}
                      disabled={currentPage <= 1 || imageLoading}
                      className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <span className="text-xs text-gray-400">Page {currentPage} of {totalPages}</span>
                    <button
                      onClick={() => fetchImage(selectedScan.id, currentPage + 1)}
                      disabled={currentPage >= totalPages || imageLoading}
                      className="p-1.5 rounded bg-gray-700 hover:bg-gray-600 disabled:opacity-50"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* Scan info */}
              <div className="text-sm">
                <p className="font-medium">{selectedScan.detected_title || selectedScan.file_name}</p>
                {selectedScan.detected_score && (
                  <p className="text-gray-400">Score: {selectedScan.detected_score}%</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  {selectedScan.source} &middot; {selectedScan.scan_date ? new Date(selectedScan.scan_date).toLocaleDateString() : 'Unknown date'}
                </p>
              </div>

              {/* Student picker */}
              <div>
                <p className="text-sm text-gray-400 mb-2">Which child?</p>
                <div className="space-y-1.5">
                  {students.map((student) => (
                    <button
                      key={student.id}
                      onClick={() => assignMutation.mutate({ scanId: selectedScan.id, studentId: student.id })}
                      disabled={assignMutation.isPending}
                      className="w-full p-2.5 text-left rounded-lg bg-surface-card hover:bg-surface-hover transition-colors flex items-center justify-between disabled:opacity-50 text-sm"
                    >
                      <span className="font-medium">{student.name}</span>
                      {assignMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              {unlinkedScans.map((scan) => (
                <button
                  key={scan.id}
                  onClick={() => handleSelect(scan)}
                  className="w-full p-2.5 text-left rounded-lg bg-surface-card/50 hover:bg-surface-hover transition-colors text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{scan.detected_title || scan.file_name}</p>
                      <p className="text-xs text-gray-400">
                        {scan.source} &middot; {scan.scan_date ? new Date(scan.scan_date).toLocaleDateString() : 'Unknown date'}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 ml-2" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
